// MyRecurringDetail — v2 port of v1's web/dashboard/src/pages/
// MyRecurringDetail.tsx. CHG-1519 / ITER-3638 (donor-portal wave 2).
//
// Donor self-service detail view for a single recurring donation.
// Consumes GET /api/v1/my-giving/recurring-donations/:id (CHG-J on
// v1 backend) plus the pause / resume / cancel mutation endpoints.
// Schedule PATCH (CHG-0047) + payment-method swap (CHG-K) UX deferred
// to a polish follow-up — the brief's minimum is "view + lifecycle
// actions". Per CHG-1473 nav.ts γ.3 placement, mounts at
// /me/recurring/:id (v2-canonical URL scheme).
//
// Uses v2's DetailPage primitive with action-aware buttons that
// branch on the recurring's status (active → pause + cancel;
// paused → resume + cancel; canceled/suspended → no actions).
// Mutations confirm via window.confirm() because the brief is for
// minimum viable port; a real ConfirmDialog primitive lands as a
// follow-up.

import type { ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DetailPage, type DetailPageConfig } from "../components/composition";
import {
  fetchRecurringDonation,
  pauseRecurring,
  resumeRecurring,
  cancelRecurring,
  type PortalRecurring,
} from "../api/my-giving";

function StatusBadge({ status }: { status: string }) {
  // Compact status pill. Color cue derives from a small lookup; unknown
  // statuses fall back to neutral. Mirrors v1's <Badge> intent without
  // pulling the full component into v2.
  const tone =
    status === "active"
      ? { bg: "#dcfce7", fg: "#166534" }
      : status === "paused"
      ? { bg: "#fef9c3", fg: "#854d0e" }
      : status === "canceled" || status === "suspended"
      ? { bg: "#fee2e2", fg: "#991b1b" }
      : { bg: "#e5e7eb", fg: "#374151" };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        fontSize: 12,
        fontWeight: 500,
        borderRadius: 999,
        background: tone.bg,
        color: tone.fg,
        textTransform: "capitalize",
      }}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
        {label}
      </span>
      <span style={{ fontSize: 14, color: "var(--color-text)" }}>{value}</span>
    </div>
  );
}

export function MyRecurringDetail() {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["me", "recurring", "detail"] });
    queryClient.invalidateQueries({ queryKey: ["me", "recurring", "list"] });
  };

  const pause = useMutation({
    mutationFn: pauseRecurring,
    onSuccess: invalidate,
  });
  const resume = useMutation({
    mutationFn: resumeRecurring,
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    mutationFn: cancelRecurring,
    onSuccess: invalidate,
  });

  const config: DetailPageConfig<PortalRecurring> = {
    idParam: "recurringId",
    queryKey: (id) => ["me", "recurring", "detail", id] as const,
    queryFn: (id) => fetchRecurringDonation(id),
    backTo: "/my-giving/recurring",
    title: (record) => {
      const desig = record.designation_name ? ` — ${record.designation_name}` : "";
      return `Recurring gift${desig}`;
    },
    subtitle: (record) => record.org_name,
    actions: (record) => {
      const id = record.id;
      const acts = [];
      if (record.status === "active") {
        acts.push({
          id: "pause",
          label: pause.isPending ? "Pausing…" : "Pause",
          onClick: () => {
            if (window.confirm("Pause this recurring gift?")) pause.mutate(id);
          },
          disabled: pause.isPending,
        });
      }
      if (record.status === "paused") {
        acts.push({
          id: "resume",
          label: resume.isPending ? "Resuming…" : "Resume",
          variant: "primary" as const,
          onClick: () => resume.mutate(id),
          disabled: resume.isPending,
        });
      }
      if (record.status !== "canceled" && record.status !== "suspended") {
        acts.push({
          id: "cancel",
          label: cancel.isPending ? "Canceling…" : "Cancel",
          variant: "secondary" as const,
          onClick: () => {
            if (
              window.confirm(
                "Cancel this recurring gift? You can start a new one any time.",
              )
            ) {
              cancel.mutate(id);
            }
          },
          disabled: cancel.isPending,
        });
      }
      return acts;
    },
    tabs: [
      {
        id: "summary",
        label: "Summary",
        render: (record, fmt) => {
          const amountMinorUnits =
            record.amount_minor_units ?? Math.round(record.amount * 100);
          return (
            <div
              style={{
                padding: 24,
                background: "var(--color-surface)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md, 8px)",
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 16,
              }}
            >
              <InfoRow
                label="Status"
                value={<StatusBadge status={record.status} />}
              />
              <InfoRow
                label="Amount"
                value={
                  <span style={{ fontWeight: 600 }}>
                    {fmt.formatCurrency(amountMinorUnits, record.currency)}
                  </span>
                }
              />
              <InfoRow label="Frequency" value={
                <span style={{ textTransform: "capitalize" }}>
                  {record.frequency.replace(/_/g, " ")}
                </span>
              } />
              <InfoRow
                label="Schedule"
                value={record.schedule_label ?? "—"}
              />
              <InfoRow label="Organization" value={record.org_name} />
              <InfoRow
                label="Designation"
                value={record.designation_name ?? "—"}
              />
              <InfoRow
                label="Next charge"
                value={
                  record.next_charge_at
                    ? fmt.formatDateTime(record.next_charge_at)
                    : "—"
                }
              />
              <InfoRow
                label="Provider"
                value={record.provider_key ?? "—"}
              />
              <InfoRow
                label="Started"
                value={
                  record.created_at
                    ? fmt.formatDate(record.created_at, "medium")
                    : "—"
                }
              />
            </div>
          );
        },
      },
    ],
  };

  return <DetailPage config={config} />;
}
